import { ENDPOINTS } from '../constants/endpoints'
import type {
  ExpenseApprovalData,
  ExpenseCategoryCreateData,
  ExpenseCategoryData,
  ExpenseCategoryUpdateData,
  ExpenseCreateData,
  ExpenseData,
  ExpenseUpdateData,
} from '../schema/expensesSchema'
import type { AxiosResponse } from 'axios'
import axiosInstance from '@/integrations/axios/axios-instance'

export const expensesApi = {
  // ===== Categories =====
  getCategories: (): Promise<
    AxiosResponse<{ categories: Array<ExpenseCategoryData> }>
  > => {
    const endpoint = ENDPOINTS.LIST_CATEGORIES
    return axiosInstance.get(endpoint)
  },
  getPublicCategories: (): Promise<
    AxiosResponse<{ categories: Array<ExpenseCategoryData> }>
  > => {
    const endpoint = `${ENDPOINTS.LIST_CATEGORIES}/public`
    return axiosInstance.get(endpoint)
  },
  getCategory: (
    categoryId: string,
  ): Promise<AxiosResponse<{ category: ExpenseCategoryData }>> => {
    const endpoint = `${ENDPOINTS.CATEGORY}/${categoryId}`
    return axiosInstance.get(endpoint)
  },
  createCategory: (
    category: ExpenseCategoryCreateData,
  ): Promise<
    AxiosResponse<{ message: string; category: ExpenseCategoryData }>
  > => {
    const endpoint = ENDPOINTS.CREATE_CATEGORY
    return axiosInstance.post(endpoint, category)
  },
  updateCategory: (
    category: ExpenseCategoryUpdateData,
  ): Promise<
    AxiosResponse<{ message: string; category: ExpenseCategoryData }>
  > => {
    const categoryId = category._id
    const endpoint = `${ENDPOINTS.CATEGORY}/${categoryId}`
    return axiosInstance.put(endpoint, category)
  },
  deleteCategory: (
    categoryId: string,
  ): Promise<AxiosResponse<{ message: string; id: string }>> => {
    const endpoint = `${ENDPOINTS.CATEGORY}/${categoryId}`
    return axiosInstance.delete(endpoint)
  },

  // ===== Expenses =====
  getExpenses: (): Promise<AxiosResponse<{ expenses: Array<ExpenseData> }>> => {
    const endpoint = ENDPOINTS.LIST_EXPENSES
    return axiosInstance.get(endpoint)
  },
  getExpense: (
    expenseId: string,
  ): Promise<AxiosResponse<{ expense: ExpenseData }>> => {
    const endpoint = `${ENDPOINTS.EXPENSE}/${expenseId}`
    return axiosInstance.get(endpoint)
  },
  createExpense: (
    expense: ExpenseCreateData,
  ): Promise<AxiosResponse<{ message: string; expense: ExpenseData }>> => {
    const endpoint = ENDPOINTS.CREATE_EXPENSE

    // Build FormData with embedded expense items structure
    const formData = new FormData()

    // Add non-array fields
    formData.append('churchId', expense.churchId)
    formData.append('requester', expense.requester)
    formData.append('requesterEmail', expense.requesterEmail)
    formData.append('date', expense.date)
    formData.append('notes', expense.notes)

    // Add expense items with embedded files using array notation
    expense.expenseItems.forEach((item, index) => {
      formData.append(`expenseItems[${index}][categoryId]`, item.categoryId)
      formData.append(
        `expenseItems[${index}][itemDescription]`,
        item.itemDescription,
      )
      formData.append(`expenseItems[${index}][amount]`, item.amount.toString())
      formData.append(`expenseItems[${index}][file]`, item.file)
    })

    return axiosInstance.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  updateExpense: (
    expense: ExpenseUpdateData,
  ): Promise<AxiosResponse<{ message: string; expense: ExpenseData }>> => {
    const expenseId = expense._id
    const endpoint = `${ENDPOINTS.EXPENSE}/${expenseId}`

    // Build FormData with embedded expense items structure
    const formData = new FormData()

    // Add non-array fields
    formData.append('churchId', expense.churchId)
    formData.append('requester', expense.requester)
    formData.append('requesterEmail', expense.requesterEmail)
    formData.append('date', expense.date)
    formData.append('notes', expense.notes)

    // Add expense items with embedded files using array notation
    expense.expenseItems.forEach((item, index) => {
      formData.append(`expenseItems[${index}][categoryId]`, item.categoryId)
      formData.append(
        `expenseItems[${index}][itemDescription]`,
        item.itemDescription,
      )
      formData.append(`expenseItems[${index}][amount]`, item.amount.toString())

      if ('file' in item && item.file instanceof File) {
        formData.append(`expenseItems[${index}][file]`, item.file)
      } else if ('_id' in item) {
        formData.append(`expenseItems[${index}][_id]`, item._id)
      }
    })

    return axiosInstance.put(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
  deleteExpense: (
    expenseId: string,
  ): Promise<AxiosResponse<{ message: string; id: string }>> => {
    const endpoint = `${ENDPOINTS.EXPENSE}/${expenseId}`
    return axiosInstance.delete(endpoint)
  },

  // ===== Approvals =====
  approveExpense: (
    approval: ExpenseApprovalData,
  ): Promise<AxiosResponse<{ message: string; expense: ExpenseData }>> => {
    const endpoint = `${ENDPOINTS.EXPENSE}/${approval._id}/approve`
    return axiosInstance.post(endpoint, approval)
  },

  // ===== Receipts =====
  downloadReceipt: (
    expenseId: string,
    receiptUrl: string,
  ): Promise<AxiosResponse<Blob>> => {
    const endpoint = `${ENDPOINTS.EXPENSE}/${expenseId}/${receiptUrl}`
    return axiosInstance.get(endpoint, {
      responseType: 'blob',
    })
  },

  // ===== Public fix-request =====
  getExpenseByToken: (
    token: string,
  ): Promise<
    AxiosResponse<{
      expense: ExpenseData
      categories: Array<ExpenseCategoryData>
    }>
  > => {
    return axiosInstance.get(`${ENDPOINTS.EXPENSE}/edit-request/${token}`)
  },
  updateExpenseByToken: (
    token: string,
    expense: ExpenseUpdateData,
  ): Promise<AxiosResponse<{ message: string; expense: ExpenseData }>> => {
    const formData = new FormData()
    formData.append('churchId', expense.churchId)
    formData.append('requester', expense.requester)
    formData.append('requesterEmail', expense.requesterEmail)
    formData.append('date', expense.date)
    formData.append('notes', expense.notes)
    expense.expenseItems.forEach((item, index) => {
      formData.append(`expenseItems[${index}][categoryId]`, item.categoryId)
      formData.append(
        `expenseItems[${index}][itemDescription]`,
        item.itemDescription,
      )
      formData.append(`expenseItems[${index}][amount]`, item.amount.toString())
      if ('file' in item && item.file instanceof File) {
        formData.append(`expenseItems[${index}][file]`, item.file)
      } else if ('_id' in item) {
        formData.append(`expenseItems[${index}][_id]`, item._id)
      }
    })
    return axiosInstance.put(
      `${ENDPOINTS.EXPENSE}/edit-request/${token}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  },
}
